import React, { useMemo } from 'react';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import {
  Box,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';

import type { ProviderSummary } from '../../types';

interface PracticeOverviewProps {
  /** List of providers to aggregate data from */
  providers?: ProviderSummary[];
}

export const PracticeOverview: React.FC<PracticeOverviewProps> = ({ providers = [] }) => {
  const theme = useTheme();

  // Aggregate data across all providers
  const aggregatedData = useMemo(() => {
    const totalNotes = providers.reduce(
      (sum, provider) => sum + (provider.total_notes_audited ?? 0),
      0,
    );
    const totalUnsigned = providers.reduce(
      (sum, provider) => sum + (provider.unsigned_count ?? 0),
      0,
    );
    const totalSigned = providers.reduce(
      (sum, provider) => sum + ((provider as any).signed_count ?? 0),
      0,
    );

    // Weighted average of compliance_percentage (weight by total_notes_audited)
    const weightedComplianceSum = providers.reduce((sum, provider) => {
      const total = provider.total_notes_audited ?? 0;
      const compliancePct = provider.compliance_percentage ?? 0;
      return sum + total * compliancePct;
    }, 0);

    // Weighted average of cpt_accuracy_pct (weight by total_notes_audited)
    const weightedCptSum = providers.reduce((sum, provider) => {
      const total = provider.total_notes_audited ?? 0;
      const cptPct = provider.cpt_accuracy_pct ?? 0;
      return sum + total * cptPct;
    }, 0);

    // Calculate weighted average rates
    const complianceRate = totalNotes > 0 ? weightedComplianceSum / totalNotes : 0;
    const cptMatchRate = totalNotes > 0 ? weightedCptSum / totalNotes : 0;

    return {
      totalNotes,
      totalUnsigned,
      totalSigned,
      complianceRate,
      cptMatchRate,
    };
  }, [providers]);

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Practice Overview
        </Typography>
        <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
        {/* Compliance Rate Card */}
        <GridItem span={2}>
          <SmallStatCard
            label="Compliance Rate"
            value={`${aggregatedData.complianceRate.toFixed(1)}%`}
          />
        </GridItem>

        {/* CPT Match Rate Card */}
        <GridItem span={2}>
          <SmallStatCard
            label="CPT Match Rate"
            value={`${aggregatedData.cptMatchRate.toFixed(1)}%`}
          />
        </GridItem>

        {/* Revenue Progress Card */}
        <GridItem span={4}>
          <ProgressStatCard
            barColor="#3b82f6" // Blue
            secondaryBarColor="#ef4444" // Red
            barValue={0}
            secondaryBarValue={0}
            stats={[
              { label: 'Total Revenue', value: '$0', dotColor: '#3b82f6' },
              { label: 'Rejected Claims', value: '$0', dotColor: '#ef4444' },
              { label: 'Unfilled Notes', value: '$0', dotColor: '#e5e7eb' },
            ]}
          />
        </GridItem>

        {/* Notes Progress Card */}
        <GridItem span={4}>
          <ProgressStatCard
            barColor="#10b981" // Green for signed
            secondaryBarColor="#fbbf24" // Yellow for unsigned
            tertiaryBarColor="#e5e7eb" // Grey for remaining/total background
            barValue={
              aggregatedData.totalNotes > 0
                ? (aggregatedData.totalSigned / aggregatedData.totalNotes) * 100
                : 0
            }
            secondaryBarValue={
              aggregatedData.totalNotes > 0
                ? (aggregatedData.totalUnsigned / aggregatedData.totalNotes) * 100
                : 0
            }
            stats={[
              {
                label: 'Signed',
                value: aggregatedData.totalSigned.toLocaleString(),
                dotColor: '#10b981', // Green for signed (matches progress bar)
              },
              {
                label: 'Unsigned',
                value: aggregatedData.totalUnsigned.toLocaleString(),
                dotColor: '#fbbf24', // Yellow for unsigned (matches progress bar)
              },
            ]}
          />
        </GridItem>
      </Box>
    </Box>
  );
};

const GridItem: React.FC<{ span: number; children: React.ReactNode }> = ({ span, children }) => (
  <Box sx={{ gridColumn: `span ${span}` }}>{children}</Box>
);

const SmallStatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
};

interface ProgressStatCardProps {
  barColor: string;
  secondaryBarColor: string;
  tertiaryBarColor?: string;
  barValue: number;
  secondaryBarValue: number;
  stats: { label: string; value: string; dotColor: string }[];
}

const ProgressStatCard: React.FC<ProgressStatCardProps> = ({
  barColor,
  secondaryBarColor,
  tertiaryBarColor = '#e5e7eb',
  barValue,
  secondaryBarValue,
  stats,
}) => {
  const theme = useTheme();
  const remainingValue = Math.max(0, 100 - barValue - secondaryBarValue);
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 3,
        height: '100%',
      }}
    >
      <Box sx={{ width: '100%', mb: 3, position: 'relative', height: 6, bgcolor: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${barValue}%`, bgcolor: barColor }} />
        <Box sx={{ position: 'absolute', left: `${barValue}%`, top: 0, height: '100%', width: `${secondaryBarValue}%`, bgcolor: secondaryBarColor }} />
        <Box sx={{ position: 'absolute', left: `${barValue + secondaryBarValue}%`, top: 0, height: '100%', width: `${remainingValue}%`, bgcolor: tertiaryBarColor }} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        {stats.map((stat, index) => (
          <Box key={index} sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stat.dotColor }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {stat.label}
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, ml: 2 }}>
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

